/**
 * Mes primes (`/extranet/:tenantSlug/primes` — fiches 22749 / 22763 / 22988).
 *
 * - `WS2_EA_TAB_QUI` is loaded with the list card's toolbar state (React
 *   Query, scoped by environment, session and filters): the card reports its
 *   search (`filtre`, debounced) and situation (`situation`) through
 *   `onFiltersChange`, each sent only when non-blank. The previous rows stay
 *   on screen while a new search loads (`keepPreviousData`).
 * - The « Documents » action of a row selects that premium and opens
 *   `extranet.premiumDocumentsPanel`; its documents (`WS2_EA_TAB_DOCS_QUI`,
 *   `id_qui` = `ID_QUI`) are fetched while a premium is selected.
 * - The page header carries the title and the « {n} primes » subtitle (once
 *   loaded — the count of the current search / situation); the list card
 *   has no title of its own.
 * - The body is wrapped in a `RuleContextProvider` exposing the base extranet
 *   variables + `premiums_count` (rows of the current search / situation).
 */

import { useCallback, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { keepPreviousData, useQuery } from '@tanstack/react-query';

import { RuleContextProvider } from '@components/shared/configurable';

import {
  buildPremiumsListParams,
  premiumsService,
  type Premium,
  type PremiumDocument,
  type PremiumsListFilters,
} from '../api/premiumsService';
import { useExtranetAuthStore } from '../auth/store';
import { PageHeader } from '../components/PageHeader';
import { buildPremiumsRuleContext } from '../components/premiums/premiumsContext';
import { premiumsCountSubtitle } from '../components/premiums/premiumsListConfig';
import {
  PremiumDocumentsPanelConfigurable as PremiumDocumentsPanel,
  PremiumsListCardConfigurable as PremiumsListCard,
} from '../components/premiums/register';
import { useExtranetBaseRuleContext } from '../contextVariables';
import { EXTRANET_PAGE_TEXT, extranetPath } from '../navigation';

const NO_PREMIUMS: readonly Premium[] = [];
const NO_DOCUMENTS: readonly PremiumDocument[] = [];
const NO_FILTERS: PremiumsListFilters = {};

/**
 * Query key of the premiums list — scoped by environment, session and the
 * effective request params (blank filters ⇒ the same key as no filter).
 */
function premiumsQueryKey(
  registrySlug: string | null,
  sessionId: number | null,
  filters: PremiumsListFilters,
): readonly unknown[] {
  return ['extranet', 'premiums', registrySlug, sessionId, buildPremiumsListParams(filters)];
}

/** Query key of a premium's documents. */
function premiumDocumentsQueryKey(
  registrySlug: string | null,
  sessionId: number | null,
  idQui: number | null,
): readonly unknown[] {
  return ['extranet', 'premium-documents', registrySlug, sessionId, idQui];
}

function errorMessage(error: Error | null): string | null {
  return error ? error.message : null;
}

export function PremiumsPage() {
  const { tenantSlug } = useParams<{ tenantSlug: string }>();
  const text = EXTRANET_PAGE_TEXT.premiums;
  const registrySlug = useExtranetAuthStore((s) => s.registrySlug);
  const sessionId = useExtranetAuthStore((s) => s.sessionId);

  // Search / situation of the list card's toolbar (server-side, fiche 22988).
  const [filters, setFilters] = useState<PremiumsListFilters>(NO_FILTERS);

  const premiumsQuery = useQuery<Premium[]>({
    queryKey: premiumsQueryKey(registrySlug, sessionId, filters),
    queryFn: () => premiumsService.fetchPremiums(registrySlug, filters),
    enabled: registrySlug !== null,
    retry: false,
    placeholderData: keepPreviousData,
  });
  const premiums = premiumsQuery.data ?? NO_PREMIUMS;

  // The premium whose documents panel is open (`null` ⇒ closed).
  const [selected, setSelected] = useState<Premium | null>(null);
  const selectedIdQui = selected?.idQui ?? null;

  const documentsQuery = useQuery<PremiumDocument[]>({
    queryKey: premiumDocumentsQueryKey(registrySlug, sessionId, selectedIdQui),
    queryFn: () => premiumsService.fetchPremiumDocuments(registrySlug, selectedIdQui ?? 0),
    enabled: registrySlug !== null && selectedIdQui !== null,
    retry: false,
  });
  const documents = selectedIdQui === null ? NO_DOCUMENTS : (documentsQuery.data ?? NO_DOCUMENTS);

  const openDocuments = useCallback((premium: Premium) => {
    setSelected(premium);
  }, []);
  const closeDocuments = useCallback(() => {
    setSelected(null);
  }, []);

  const baseContext = useExtranetBaseRuleContext();
  const premiumsCount = premiumsQuery.data?.length;
  const ruleContext = useMemo(
    () => buildPremiumsRuleContext(baseContext, premiumsCount),
    [baseContext, premiumsCount],
  );

  return (
    <RuleContextProvider value={ruleContext}>
      <div data-testid="extranet-page-premiums">
        <PageHeader
          title={text.title}
          subtitle={premiumsCount === undefined ? undefined : premiumsCountSubtitle(premiumsCount)}
          backTo={extranetPath(tenantSlug)}
          breadcrumbs={[{ key: 'primes', label: text.breadcrumb }]}
        />
        <div className="mx-auto max-w-7xl px-4 py-6 sm:px-6 lg:px-8">
          <PremiumsListCard
            premiums={premiums}
            loading={premiumsQuery.isLoading}
            error={errorMessage(premiumsQuery.error)}
            onOpenDocuments={openDocuments}
            onFiltersChange={setFilters}
          />
        </div>
        <PremiumDocumentsPanel
          open={selected !== null}
          onClose={closeDocuments}
          idQui={selectedIdQui}
          numQui={selected?.reference ?? documents[0]?.numQui ?? null}
          documents={documents}
          loading={selectedIdQui !== null && documentsQuery.isLoading}
          error={selectedIdQui === null ? null : errorMessage(documentsQuery.error)}
        />
      </div>
    </RuleContextProvider>
  );
}
